import { useSearchParams } from 'next/navigation';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import useQueryTransportsSelector from '@/integrations/content/content-modules/transports-selector/transports-selector.hooks';
import { useHotelContext } from '@/libraries/contexts/hotel-context/hotel-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { useSearchContext } from '@/libraries/contexts/search-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import { getSearchParameters } from '@/libraries/getters/get-search-parameters';
import { getUrlParameters } from '@/libraries/getters/get-url-parameters';
import TransportsSelectorItem, {
  TransportsSelectorItemProps,
} from './transports-selector-item/transports-selector-item';
import styles from './transports-selector.module.scss';

export interface TransportsSelectorProps {
  /** The text to show as a headline */
  headline?: Content.Components['TransportsSelectorModuleHeadlessDto']['headline'];
  /** The UUID of the module */
  id: Content.Components['TransportsSelectorModuleHeadlessDto']['id'];
  /** The UUID of the current page */
  pageId: Content.Components['TransportsSelectorModuleHeadlessDto']['pageId'];
  /** The text to show about upgradeable transports */
  upgradeableText?: Content.Components['TransportsSelectorModuleHeadlessDto']['upgradeableText'];
}

const getIconName = (name: string): IconName => {
  switch (name) {
    case 'train':
      return 'Train';

    case 'bus':
      return 'Bus';

    case 'plane':
      return 'PlaneTakeOff';

    default:
      return 'Car';
  }
};

const TransportsSelector = ({
  headline,
  id,
  pageId,
  upgradeableText,
}: TransportsSelectorProps) => {
  const searchParams = useSearchParams();
  const { parameters, totalPrice } = useHotelContext();
  const { bonusCodes } = useSearchContext();
  const { offerDetails } = usePageDetailsContext();
  const currencyFormatter = useCurrencyFormatter();

  const originalTransportMode =
    (getUrlParameters(
      getSearchParameters(searchParams!),
    )?.OriginalTransportMode?.toLowerCase() as Content.Components['TransportMode']) ||
    offerDetails?.transportMode;

  const {
    data: dataTransportsSelector,
    isFetching: isFetchingTransportsSelector,
  } = useQueryTransportsSelector({
    enabled: !!originalTransportMode,
    parameters: parameters && {
      ...parameters,
      ...(parameters?.bonusCodes ? {} : bonusCodes ? { bonusCodes } : {}),
      id,
      originalTransportMode: originalTransportMode,
      pageId,
    },
  });

  const getPriceText = (offerPrice: number): string | undefined => {
    if (!totalPrice) return;

    if (totalPrice === offerPrice) return;

    const priceDifference = totalPrice - offerPrice;
    const priceRelativeSymbol = priceDifference > 0 ? '-' : '+';

    return `${priceRelativeSymbol}${currencyFormatter(Math.abs(priceDifference))}`;
  };

  const transportOptions: TransportsSelectorItemProps[] =
    dataTransportsSelector?.tabs.map(tab => ({
      icon: getIconName(tab.departure.departureTransportMode),
      label: tab.departure.departureName,
      state: tab.isSelected ? 'active' : 'default',
      text: getPriceText(tab.offer.price.totalPrice),
      url: tab.offer.searchUrl,
    })) || [];

  const hasNoResults =
    !isFetchingTransportsSelector && !dataTransportsSelector?.tabs.length;

  const renderResults = () => {
    if (isFetchingTransportsSelector) {
      return (
        <div className={styles.skeleton}>
          <SkeletonLoader
            borderRadius="4px"
            className={styles.transport}
            height="80px"
            width="150px"
          />

          <SkeletonLoader
            borderRadius="4px"
            className={styles.transport}
            height="80px"
            width="150px"
          />
        </div>
      );
    }

    return (
      <div className={styles.transports}>
        {transportOptions.map((transport, transportIndex) => (
          <TransportsSelectorItem
            key={transportIndex}
            className={styles.transport}
            {...transport}
          />
        ))}
      </div>
    );
  };

  if (hasNoResults) return null;

  return (
    <div className={styles.component}>
      {headline && (
        <Typography className={styles.headline} tagStyle="bodyMediumBold">
          {headline}
        </Typography>
      )}

      {upgradeableText && (
        <Typography tagStyle="bodyMedium">{upgradeableText}</Typography>
      )}

      {renderResults()}
    </div>
  );
};

TransportsSelector.displayName = 'TransportsSelector';

export default TransportsSelector;
